import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { STATUS_COLORS } from './colors';
import { formatDay } from './formatDay';
import { pickedDayToUtcMidnight } from '../../utils/dateFormat';

const SKIPPED_COLOR = '#6b7280';
const STATUSES = [
  { key: 'pass', name: 'Pass', color: STATUS_COLORS.passed },
  { key: 'fail', name: 'Fail', color: STATUS_COLORS.fail },
  { key: 'warning', name: 'Warning', color: STATUS_COLORS.warning },
  { key: 'skipped', name: 'Skipped', color: SKIPPED_COLOR },
];

const utcTime = (iso) => {
  const d = new Date(iso);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
};

// One row per day; each of that day's runs (oldest first) fills a "slot" so it becomes its own
// stacked bar next to the day's other runs: pass_1/fail_1/... for the 1st run, pass_2/... for the 2nd.
function groupRunsByDay(runs, range) {
  // Seed every calendar day of the picked range so the X axis always spans it, runs or not.
  const byDay = new Map();
  if (range?.start && range?.end) {
    const end = pickedDayToUtcMidnight(range.end).getTime();
    for (let t = pickedDayToUtcMidnight(range.start).getTime(); t <= end; t += 24 * 60 * 60 * 1000) {
      byDay.set(new Date(t).toISOString().slice(0, 10), []);
    }
  }
  [...runs]
    .sort((a, b) => new Date(a.startedAt) - new Date(b.startedAt))
    .forEach((run) => {
      const day = run.startedAt.slice(0, 10);
      byDay.set(day, [...(byDay.get(day) ?? []), run]);
    });

  const slots = Math.max(1, ...[...byDay.values()].map((r) => r.length));
  const rows = [...byDay].map(([day, dayRuns]) => {
    const row = { day, runs: dayRuns };
    dayRuns.forEach((run, i) => STATUSES.forEach(({ key }) => { row[`${key}_${i + 1}`] = run[key]; }));
    return row;
  });
  return { rows, slots };
}

function RunsTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const { day, runs } = payload[0].payload;
  return (
    <div className="sense-shield-run-tooltip">
      <div className="sense-shield-run-tooltip-day">{formatDay(day)}</div>
      {runs.length === 0 && <div>No runs</div>}
      {runs.map((run) => (
        <div key={run.runId}>
          <strong>{utcTime(run.startedAt)}</strong> · Pass {run.pass} · Fail {run.fail} · Warning {run.warning}
          {run.skipped > 0 && ` · Skipped ${run.skipped}`}
        </div>
      ))}
    </div>
  );
}

function OutcomesPerRunChart({ data, range }) {
  const { rows, slots } = useMemo(() => groupRunsByDay(data || [], range), [data, range]);
  const hasSkips = (data || []).some((r) => r.skipped > 0);
  const statuses = hasSkips ? STATUSES : STATUSES.filter((s) => s.key !== 'skipped');

  return (
    <div>
      <div className="sense-shield-panel-title">Execution Overview</div>
      <p className="sense-shield-panel-description">
        One stacked bar per run (pass, fail, warning{hasSkips ? ', skipped' : ''}), grouped by day
      </p>
      <div className="sense-shield-panel-content">
        {(data || []).length === 0 ? (
          <div className="sense-shield-empty">No run data available</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={rows} margin={{ top: 30, right: 20, left: 0, bottom: 10 }} barCategoryGap="20%" barGap={4}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={formatDay} interval="preserveStartEnd" minTickGap={16} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<RunsTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
              <Legend verticalAlign="top" align="left" wrapperStyle={{ fontSize: 12 }} />
              {Array.from({ length: slots }, (_, slot) =>
                statuses.map(({ key, name, color }) => (
                  <Bar
                    key={`${key}_${slot + 1}`}
                    dataKey={`${key}_${slot + 1}`}
                    name={name}
                    stackId={`run_${slot + 1}`}
                    fill={color}
                    legendType={slot === 0 ? 'square' : 'none'}
                  />
                ))
              )}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export default OutcomesPerRunChart;
